'use client';

import { useState } from 'react';
import { Star } from 'lucide-react';
import type { Dictionary } from '@/lib/i18n/dictionaries/en';

interface StarRatingProps {
  value: number;
  onChange: (rating: number) => void;
  size?: 'sm' | 'md' | 'lg';
  readonly?: boolean;
  dict?: Dictionary;
}

const SIZE_MAP = {
  sm: 'h-4 w-4',
  md: 'h-6 w-6',
  lg: 'h-8 w-8',
};

// Fallback English strings so any call site that hasn't been updated to
// pass `dict` yet still renders (rather than throwing on undefined).
const FALLBACK = {
  starRatingGroupLabel: 'Star rating',
  rateStarsTemplate: 'Rate {n} {word}',
  starWord: 'star',
  starsWord: 'stars',
  starsOutOfFiveTemplate: '{n} out of 5 stars',
};

export default function StarRating({
  value,
  onChange,
  size = 'md',
  readonly = false,
  dict,
}: StarRatingProps) {
  const [hovered, setHovered] = useState(0);
  const t = dict?.common ?? FALLBACK;

  const active = hovered || value;

  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => !readonly && setHovered(0)}
      role={readonly ? 'img' : 'group'}
      aria-label={readonly ? t.starsOutOfFiveTemplate.replace('{n}', String(value)) : t.starRatingGroupLabel}
    >
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          aria-label={readonly ? undefined : t.rateStarsTemplate.replace('{n}', String(star)).replace('{word}', star > 1 ? t.starsWord : t.starWord)}
          className={`transition-transform duration-100 ${
            readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110 focus:outline-none focus:scale-110'
          }`}
        >
          <Star
            className={`${SIZE_MAP[size]} transition-colors duration-100 ${
              star <= active
                ? 'fill-yellow-400 text-yellow-400'
                : 'fill-transparent text-textSecondary'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

/** Static display star row — no interaction */
export function StarDisplay({ value, size = 'sm', dict }: { value: number; size?: 'sm' | 'md' | 'lg'; dict?: Dictionary }) {
  const t = dict?.common ?? FALLBACK;
  return (
    <div className="flex items-center gap-0.5" role="img" aria-label={t.starsOutOfFiveTemplate.replace('{n}', String(value))}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`${SIZE_MAP[size]} ${
            star <= Math.round(value)
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-transparent text-textSecondary'
          }`}
        />
      ))}
    </div>
  );
}
